/**
 * ManageAppointmentPage (Jose 2026-10-22) — NO-ACCOUNT self-serve manage.
 *
 * Opened from the link in every confirmation + 24-hour reminder (/manage/:token). The token is an
 * unguessable Firestore id; `apptManageTokens/{token}` carries a display snapshot so this page never
 * touches the PII-bearing appointment directly. Cancel + Reschedule go through the `manageAppointment`
 * callable (admin SDK does the privileged write). Works whether or not the client has an account.
 *
 * LATE WINDOW (Jose): inside 1 hour of the appointment, self-serve is blocked — the client must CALL
 * (texting is too easy to miss) or manage from their account. A blocked attempt is logged server-side
 * so staff know the client wanted out (no-show risk). Self-contained styling, mirrors ConfirmAppointmentPage.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { bookingService } from '../services/bookingService';

const SHOP_PHONE_E164 = '+19735208440';          // SLIK published line
const SHOP_PHONE_PRETTY = '(973) 520-8440';
const PORTAL_MANAGE_URL = 'https://my.slikbarberlounge.com/appointments';
const LATE_WINDOW_MIN = 60;

interface TokenDoc {
  appointmentId: string;
  barberId?: string; barberName?: string;
  service?: string; serviceName?: string;
  date?: string; time?: string; duration?: number;
  clientName?: string; status?: string;
}

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 16, background: 'linear-gradient(160deg,#0f172a 0%,#1e1b4b 60%,#312e81 100%)',
  fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
};
const card: React.CSSProperties = {
  width: '100%', maxWidth: 440, background: '#fff', borderRadius: 24,
  boxShadow: '0 24px 60px rgba(0,0,0,.4)', overflow: 'hidden',
};
const btnPrimary: React.CSSProperties = {
  width: '100%', padding: '15px 18px', borderRadius: 14, border: 'none',
  background: '#4f46e5', color: '#fff', fontWeight: 800, fontSize: 16, cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  width: '100%', padding: '15px 18px', borderRadius: 14, border: '2px solid #e2e8f0',
  background: '#fff', color: '#334155', fontWeight: 700, fontSize: 15, cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  width: '100%', padding: '15px 18px', borderRadius: 14, border: '2px solid #fecaca',
  background: '#fff', color: '#b91c1c', fontWeight: 700, fontSize: 15, cursor: 'pointer',
};

function dayLabel(d?: string): string {
  if (!d) return '';
  try { return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
  catch { return d; }
}
function timeLabel(t?: string): string {
  const s = String(t || '').trim();
  if (!s) return '';
  if (/am|pm/i.test(s)) return s.toUpperCase().replace(/\s+/g, ' ');
  const [h, m] = s.split(':').map(Number);
  if (Number.isNaN(h)) return s;
  const period = h >= 12 ? 'PM' : 'AM';
  const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hh}:${String(m || 0).padStart(2, '0')} ${period}`;
}
const ymd = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Device-clock estimate of minutes until the appointment — for choosing which panel to show. The server
// re-checks authoritatively, so this only needs to be close.
function minutesUntil(date?: string, time?: string): number {
  if (!date || !time) return 100000;
  try {
    const [hRaw, mRaw] = (() => {
      const s = String(time).trim();
      const ap = /pm/i.test(s) ? 12 : 0; const am12 = /am/i.test(s) && /^12/.test(s);
      const [h, m] = s.replace(/\s*[ap]m/i, '').split(':').map(Number);
      let hh = h + (ap && h !== 12 ? 12 : 0); if (am12) hh = 0;
      return [hh, m || 0];
    })();
    const dt = new Date(`${date}T00:00:00`); dt.setHours(hRaw, mRaw, 0, 0);
    return Math.round((dt.getTime() - Date.now()) / 60000);
  } catch { return 100000; }
}

type View = 'loading' | 'notfound' | 'ready' | 'cancelled' | 'rescheduled' | 'late';

const ManageAppointmentPage: React.FC = () => {
  const { token = '' } = useParams();
  const [view, setView] = useState<View>('loading');
  const [tok, setTok] = useState<TokenDoc | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [newDate, setNewDate] = useState('');          // picking a reschedule time
  const [showCal, setShowCal] = useState(false);       // mini-calendar for dates beyond the 2-week chips
  const [slots, setSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [mode, setMode] = useState<'home' | 'reschedule'>('home');

  const fn = useMemo(() => httpsCallable(getFunctions(), 'manageAppointment'), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'apptManageTokens', token));
        if (!alive) return;
        if (!snap.exists()) { setView('notfound'); return; }
        const t = snap.data() as TokenDoc;
        setTok(t);
        if (t.status === 'cancelled') { setView('cancelled'); return; }
        setView(minutesUntil(t.date, t.time) <= LATE_WINDOW_MIN ? 'late' : 'ready');
      } catch { if (alive) setView('notfound'); }
    })();
    return () => { alive = false; };
  }, [token]);

  // next 14 days for the reschedule picker
  const dates = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    const base = new Date();
    for (let i = 0; i < 14; i++) {
      const d = new Date(base); d.setDate(base.getDate() + i);
      out.push({ value: ymd(d), label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) });
    }
    return out;
  }, []);

  useEffect(() => {
    if (mode !== 'reschedule' || !newDate || !tok?.barberId) { setSlots([]); return; }
    let alive = true;
    setLoadingSlots(true);
    bookingService.getAvailableSlots(tok.barberId, newDate, undefined, Number(tok.duration) || 30)
      .then((s) => { if (alive) setSlots(s); })
      .catch(() => { if (alive) setSlots([]); })
      .finally(() => { if (alive) setLoadingSlots(false); });
    return () => { alive = false; };
  }, [mode, newDate, tok]);

  const doCancel = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await fn({ token, action: 'cancel' });
      setView('cancelled');
    } catch (e: any) {
      if (String(e?.message || '').includes('LATE_WINDOW')) setView('late');
      else setError(e?.message || 'Could not cancel. Please try again.');
    } finally { setBusy(false); setConfirmCancel(false); }
  };

  const doReschedule = async (time: string) => {
    if (busy || !newDate) return;
    setBusy(true); setError('');
    try {
      const res: any = await fn({ token, action: 'reschedule', newDate, newTime: time });
      setTok((prev) => prev ? { ...prev, date: res?.data?.date || newDate, time: res?.data?.time || time } : prev);
      setView('rescheduled');
    } catch (e: any) {
      if (String(e?.message || '').includes('LATE_WINDOW')) setView('late');
      else setError(e?.message || 'That time didn’t take. Please pick another.');
    } finally { setBusy(false); }
  };

  const header = (
    <div style={{ padding: '18px 24px 0', textAlign: 'center' }}>
      <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 2, color: '#6366f1' }}>SLIK BARBER LOUNGE</span>
    </div>
  );

  const apptLine = tok && (
    <div style={{ textAlign: 'center', padding: '8px 24px 0' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{dayLabel(tok.date)} · {timeLabel(tok.time)}</div>
      <div style={{ fontSize: 15, color: '#475569', marginTop: 4 }}>
        {tok.serviceName || tok.service || 'Appointment'}{tok.barberName ? ` · with ${String(tok.barberName).split(' ')[0]}` : ''}
      </div>
    </div>
  );

  let body: React.ReactNode = null;

  if (view === 'loading') body = <Centered>Loading your appointment…</Centered>;
  else if (view === 'notfound') body = <Centered>This link is no longer valid. If you need help, call us at <a href={`tel:${SHOP_PHONE_E164}`} style={{ color: '#4f46e5', fontWeight: 700 }}>{SHOP_PHONE_PRETTY}</a>.</Centered>;
  else if (view === 'cancelled') body = (
    <div style={{ padding: '36px 28px', textAlign: 'center' }}>
      <div style={{ fontSize: 52, marginBottom: 8 }}>✓</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>Appointment cancelled</h1>
      <p style={{ color: '#475569', fontSize: 15, margin: 0 }}>Thanks for letting us know. Book again anytime at <a href="https://book.slikbarberlounge.com/book" style={{ color: '#4f46e5', fontWeight: 700 }}>book.slikbarberlounge.com</a>.</p>
    </div>
  );
  else if (view === 'rescheduled') body = (
    <div style={{ padding: '36px 28px', textAlign: 'center' }}>
      <div style={{ fontSize: 52, marginBottom: 8 }}>✅</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>You’re rescheduled!</h1>
      <p style={{ color: '#475569', fontSize: 16, margin: 0 }}>See you <strong>{dayLabel(tok?.date)}</strong> at <strong>{timeLabel(tok?.time)}</strong>{tok?.barberName ? <> with {String(tok.barberName).split(' ')[0]}</> : null}. 💈</p>
    </div>
  );
  else if (view === 'late') body = (
    <div style={{ padding: '20px 24px 28px' }}>
      {apptLine}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 14, padding: '14px 16px', margin: '18px 0', color: '#92400e', fontSize: 14, lineHeight: 1.5, textAlign: 'center' }}>
        It’s less than an hour before your appointment, so we can’t change it online. Please give us a quick call and we’ll take care of it.
      </div>
      <a href={`tel:${SHOP_PHONE_E164}`} style={{ ...btnPrimary, display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 12 }}>📞 Call {SHOP_PHONE_PRETTY}</a>
      <a href={PORTAL_MANAGE_URL} style={{ ...btnGhost, display: 'block', textAlign: 'center', textDecoration: 'none' }}>Have an account? Manage it there →</a>
    </div>
  );
  else if (view === 'ready') {
    body = (
      <div style={{ padding: '20px 24px 28px' }}>
        {apptLine}
        {error && <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', fontSize: 13, margin: '16px 0 0' }}>{error}</div>}

        {mode === 'home' && !confirmCancel && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
            <button style={btnPrimary} disabled={busy} onClick={() => { setMode('reschedule'); setError(''); }}>Reschedule</button>
            <button style={btnDanger} disabled={busy} onClick={() => setConfirmCancel(true)}>Cancel appointment</button>
            <a href={PORTAL_MANAGE_URL} style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, textDecoration: 'none', marginTop: 4 }}>Have an account? View all your appointments →</a>
          </div>
        )}

        {mode === 'home' && confirmCancel && (
          <div style={{ marginTop: 20 }}>
            <p style={{ color: '#334155', fontSize: 15, textAlign: 'center', margin: '0 0 16px' }}>Cancel your appointment on <strong>{dayLabel(tok?.date)}</strong> at <strong>{timeLabel(tok?.time)}</strong>?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button style={btnDanger} disabled={busy} onClick={doCancel}>{busy ? 'Cancelling…' : 'Yes, cancel it'}</button>
              <button style={btnGhost} disabled={busy} onClick={() => setConfirmCancel(false)}>Keep my appointment</button>
            </div>
          </div>
        )}

        {mode === 'reschedule' && (
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Pick a new time</span>
              <button onClick={() => { setMode('home'); setNewDate(''); setError(''); }} style={{ background: 'none', border: 'none', color: '#6366f1', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>← Back</button>
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
              {dates.map((d) => (
                <button key={d.value} onClick={() => { setNewDate(d.value); setShowCal(false); }} style={{
                  flex: '0 0 auto', padding: '8px 12px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  border: newDate === d.value ? '2px solid #4f46e5' : '2px solid #e2e8f0',
                  background: newDate === d.value ? '#eef2ff' : '#fff', color: newDate === d.value ? '#312e81' : '#334155',
                }}>{d.label}</button>
              ))}
            </div>
            <button onClick={() => setShowCal((v) => !v)} style={{
              display: 'flex', alignItems: 'center', gap: 6, margin: '2px 0 0', background: 'none', border: 'none',
              color: '#4f46e5', fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '4px 2px',
            }}>📅 {showCal ? 'Hide calendar' : 'Pick another date'}</button>
            {showCal && <MiniCalendar selected={newDate} onPick={(v) => { setNewDate(v); setShowCal(false); }} />}
            <div style={{ marginTop: 14 }}>
              {!newDate && <p style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center' }}>Choose a day above.</p>}
              {newDate && loadingSlots && <p style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center' }}>Finding open times…</p>}
              {newDate && !loadingSlots && slots.length === 0 && <p style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center' }}>No open times that fit your service that day — try another day.</p>}
              {newDate && !loadingSlots && slots.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                  {slots.map((s) => (
                    <button key={s} disabled={busy} onClick={() => doReschedule(s)} style={{
                      padding: '12px 6px', borderRadius: 10, border: '2px solid #c7d2fe', background: '#eef2ff',
                      color: '#312e81', fontWeight: 700, fontSize: 14, cursor: busy ? 'default' : 'pointer', opacity: busy ? .5 : 1,
                    }}>{timeLabel(s)}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        {header}
        {body}
      </div>
    </div>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '48px 28px', textAlign: 'center', color: '#475569', fontSize: 16, lineHeight: 1.5 }}>{children}</div>
);

// Lightweight month-grid calendar for picking a reschedule date further out than the 2-week chips.
// Past days are disabled; selecting a day hands the YYYY-MM-DD back up (same state the chips drive).
const MiniCalendar: React.FC<{ selected: string; onPick: (ymdStr: string) => void }> = ({ selected, onPick }) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr = ymd(today);
  const [cursor, setCursor] = useState(() => {
    // open on the selected month if any, else this month
    if (selected) { const d = new Date(`${selected}T12:00:00`); return new Date(d.getFullYear(), d.getMonth(), 1); }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const y = cursor.getFullYear(), mo = cursor.getMonth();
  const firstDow = new Date(y, mo, 1).getDay();
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const canPrev = new Date(y, mo, 1) > new Date(today.getFullYear(), today.getMonth(), 1);
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, mo, d));
  const navBtn = (dir: -1 | 1, disabled: boolean) => (
    <button disabled={disabled} onClick={() => setCursor(new Date(y, mo + dir, 1))} style={{
      border: 'none', background: disabled ? '#f1f5f9' : '#eef2ff', color: disabled ? '#cbd5e1' : '#4f46e5',
      width: 34, height: 34, borderRadius: 9, fontSize: 18, fontWeight: 800, cursor: disabled ? 'default' : 'pointer', lineHeight: 1,
    }}>{dir < 0 ? '‹' : '›'}</button>
  );
  return (
    <div style={{ border: '2px solid #e2e8f0', borderRadius: 14, padding: 12, marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        {navBtn(-1, !canPrev)}
        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        {navBtn(1, false)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, textAlign: 'center' }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', padding: '2px 0' }}>{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const v = ymd(d);
          const past = v < todayStr;
          const sel = v === selected;
          return (
            <button key={i} disabled={past} onClick={() => onPick(v)} style={{
              aspectRatio: '1 / 1', borderRadius: 9, fontSize: 14, fontWeight: 700,
              border: sel ? '2px solid #4f46e5' : '1px solid transparent',
              background: sel ? '#4f46e5' : past ? 'transparent' : '#f8fafc',
              color: sel ? '#fff' : past ? '#cbd5e1' : '#334155',
              cursor: past ? 'default' : 'pointer',
            }}>{d.getDate()}</button>
          );
        })}
      </div>
    </div>
  );
};

export default ManageAppointmentPage;
